import {
  LEARNING_FORMATS,
  LEARNING_PROFILE_CATALOG,
  type LearningFormat,
  type LearningProfile,
  type LocalizedText,
} from "./forfeoIntegratedLearningContract.ts";

export const INTEGRATED_JOB_AIDS_SCHEMA_VERSION = "forfeo-lot4i-i3-v1" as const;

export type JobAidStep = {
  id: string;
  title: LocalizedText;
  detail: LocalizedText;
};

export type IntegratedTrainingAssets = {
  pdf: string;
  presentation: string;
  immersiveDemo: string;
};

export type IntegratedJobAid = {
  profile: LearningProfile;
  slug: string;
  title: LocalizedText;
  summary: LocalizedText;
  steps: readonly JobAidStep[];
  format: "stepByStepGuide";
  status: "available";
  contextualRoute: string;
  assets: IntegratedTrainingAssets;
  financialAuthority: "GIFTSHOP";
};

const step = (id: string, titleFr: string, titleEn: string, detailFr: string, detailEn: string): JobAidStep => ({
  id,
  title: { fr: titleFr, en: titleEn },
  detail: { fr: detailFr, en: detailEn },
});

const assets = (slug: string, profile: LearningProfile): IntegratedTrainingAssets => ({
  pdf: `/formation/job-aids/${slug}.pdf`,
  presentation: `/formation/presentations/${slug}.pptx`,
  immersiveDemo: `/formation/immersive-demo/?profil=${profile}`,
});

export const INTEGRATED_JOB_AIDS: readonly IntegratedJobAid[] = [
  {
    profile: "merchant",
    slug: "merchant-daily-operations",
    title: { fr: "Démarrer les opérations marchandes", en: "Start merchant operations" },
    summary: {
      fr: "Configurer une offre, suivre les commandes officielles et obtenir de l’aide sans modifier les écritures financières.",
      en: "Configure an offer, track official orders and get help without changing financial records.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=merchant",
    assets: assets("merchant-daily-operations", "merchant"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("merchant-1", "Vérifier le profil", "Verify the profile", "Confirmez les coordonnées et les accès de votre espace marchand.", "Confirm the contact details and access to your merchant workspace."),
      step("merchant-2", "Préparer l’offre", "Prepare the offer", "Complétez les informations visibles par le client et utilisez les contrôles de prévisualisation.", "Complete the customer-visible information and use the preview controls."),
      step("merchant-3", "Suivre les commandes", "Track orders", "Consultez les statuts officiels fournis par Giftshop sans les remplacer localement.", "Review the official statuses supplied by Giftshop without overriding them locally."),
      step("merchant-4", "Demander du soutien", "Request support", "Notez la référence du dossier et transmettez-la au soutien si une reprise est nécessaire.", "Record the case reference and send it to support if recovery is needed."),
    ],
  },
  {
    profile: "prp",
    slug: "prp-partner-follow-up",
    title: { fr: "Accompagner un partenaire régional", en: "Support a regional partner" },
    summary: {
      fr: "Suivre l’intégration d’un partenaire, consulter ses dossiers et escalader une exception en toute sécurité.",
      en: "Track partner onboarding, review cases and safely escalate an exception.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=prp",
    assets: assets("prp-partner-follow-up", "prp"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("prp-1", "Ouvrir le dossier", "Open the case", "Choisissez le partenaire concerné et vérifiez l’état de son intégration.", "Select the partner and verify their onboarding status."),
      step("prp-2", "Valider les informations", "Validate information", "Repérez les champs incomplets sans copier de données sensibles dans vos notes.", "Identify incomplete fields without copying sensitive data into notes."),
      step("prp-3", "Consulter les statuts", "Review statuses", "Consultez les statuts de commission officiels en lecture seule.", "Review official commission statuses in read-only mode."),
      step("prp-4", "Escalader l’exception", "Escalate the exception", "Créez une demande de soutien avec la référence du dossier et l’étape bloquée.", "Create a support request with the case reference and blocked step."),
    ],
  },
  {
    profile: "enterprise",
    slug: "enterprise-campaign",
    title: { fr: "Préparer une campagne entreprise", en: "Prepare a corporate campaign" },
    summary: {
      fr: "Organiser une campagne, préparer les destinataires et suivre la commande officielle.",
      en: "Organize a campaign, prepare recipients and track the official order.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=enterprise",
    assets: assets("enterprise-campaign", "enterprise"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("enterprise-1", "Définir la campagne", "Define the campaign", "Nommez la campagne et précisez son objectif interne.", "Name the campaign and define its internal goal."),
      step("enterprise-2", "Préparer les destinataires", "Prepare recipients", "Vérifiez les destinataires et les autorisations avant l’importation.", "Verify recipients and permissions before import."),
      step("enterprise-3", "Contrôler l’aperçu", "Review the preview", "Relisez le contenu, la langue et la présentation avant de continuer.", "Review the content, language and presentation before continuing."),
      step("enterprise-4", "Suivre la commande", "Track the order", "Utilisez uniquement le statut officiel Giftshop et contactez le soutien en cas d’écart.", "Use only the official Giftshop status and contact support if there is a discrepancy."),
    ],
  },
  {
    profile: "landlord",
    slug: "landlord-habitat-workspace",
    title: { fr: "Gérer l’espace propriétaire", en: "Manage the landlord workspace" },
    summary: {
      fr: "Compléter le profil, suivre un logement et consulter les statuts officiels liés au bail.",
      en: "Complete the profile, track a property and review official lease-related statuses.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=landlord",
    assets: assets("landlord-habitat-workspace", "landlord"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("landlord-1", "Compléter le profil", "Complete the profile", "Vérifiez les coordonnées et les renseignements requis pour votre rôle.", "Verify the contact details and information required for your role."),
      step("landlord-2", "Ouvrir le logement", "Open the property", "Sélectionnez le logement et confirmez que le bon dossier de bail est affiché.", "Select the property and confirm that the correct lease record is displayed."),
      step("landlord-3", "Consulter le statut", "Review the status", "Consultez les statuts officiels sans créer de confirmation parallèle.", "Review official statuses without creating a parallel confirmation."),
      step("landlord-4", "Signaler une anomalie", "Report an issue", "Transmettez la référence du bail au soutien sans inclure de secret ni de donnée financière brute.", "Send the lease reference to support without including secrets or raw financial data."),
    ],
  },
  {
    profile: "tenant",
    slug: "tenant-habitat-path",
    title: { fr: "Utiliser l’espace locataire", en: "Use the tenant workspace" },
    summary: {
      fr: "Vérifier le profil, consulter le bail et utiliser le parcours officiel lorsqu’une action est requise.",
      en: "Verify the profile, review the lease and use the official path when action is required.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=tenant",
    assets: assets("tenant-habitat-path", "tenant"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("tenant-1", "Vérifier le profil", "Verify the profile", "Confirmez que vos coordonnées et votre rôle sont exacts.", "Confirm that your contact details and role are correct."),
      step("tenant-2", "Consulter le bail", "Review the lease", "Ouvrez le dossier du logement et relisez les informations affichées.", "Open the property record and review the displayed information."),
      step("tenant-3", "Suivre le parcours officiel", "Follow the official path", "Utilisez uniquement les boutons et statuts officiels fournis dans l’application.", "Use only the official buttons and statuses provided in the application."),
      step("tenant-4", "Obtenir de l’aide", "Get help", "Conservez la référence du dossier et décrivez l’étape qui ne fonctionne pas.", "Keep the case reference and describe the step that is not working."),
    ],
  },
  {
    profile: "administrator",
    slug: "administrator-platform-review",
    title: { fr: "Superviser la plateforme", en: "Oversee the platform" },
    summary: {
      fr: "Consulter les consoles opérationnelles, la gouvernance des contenus et les incidents sans mutation financière dans le navigateur.",
      en: "Review operations consoles, content governance and incidents without browser-side financial mutations.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=administrator",
    assets: assets("administrator-platform-review", "administrator"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("administrator-1", "Ouvrir la console", "Open the console", "Accédez à la console correspondant au domaine à examiner.", "Open the console for the domain being reviewed."),
      step("administrator-2", "Filtrer les signaux", "Filter signals", "Réduisez la vue à la fenêtre et au statut pertinents.", "Narrow the view to the relevant window and status."),
      step("administrator-3", "Vérifier la gouvernance", "Verify governance", "Contrôlez le propriétaire, la révision et la traçabilité du contenu.", "Check content ownership, review and traceability."),
      step("administrator-4", "Documenter la décision", "Document the decision", "Ajoutez une note factuelle et utilisez le chemin serveur autorisé pour toute action assistée.", "Add a factual note and use the authorized server path for any assisted action."),
    ],
  },
  {
    profile: "support",
    slug: "support-safe-diagnosis",
    title: { fr: "Diagnostiquer une demande de soutien", en: "Diagnose a support request" },
    summary: {
      fr: "Recevoir un dossier, diagnostiquer sans données sensibles, escalader et documenter la résolution.",
      en: "Receive a case, diagnose without sensitive data, escalate and document the resolution.",
    },
    format: "stepByStepGuide",
    status: "available",
    contextualRoute: "/aide/formation?profil=support",
    assets: assets("support-safe-diagnosis", "support"),
    financialAuthority: "GIFTSHOP",
    steps: [
      step("support-1", "Qualifier la demande", "Classify the request", "Confirmez le profil, le parcours et l’étape touchée.", "Confirm the profile, journey and affected step."),
      step("support-2", "Reproduire sans risque", "Reproduce safely", "Utilisez un environnement local et des données fictives lorsque la reproduction est nécessaire.", "Use a local environment and fictional data when reproduction is needed."),
      step("support-3", "Escalader au bon propriétaire", "Escalate to the right owner", "Acheminez le dossier au propriétaire du domaine avec sa référence de corrélation.", "Route the case to the domain owner with its correlation reference."),
      step("support-4", "Documenter la résolution", "Document the resolution", "Consignez le résultat, la preuve minimale et la prochaine action autorisée.", "Record the outcome, minimum evidence and next authorized action."),
    ],
  },
] as const;

export const LEARNING_FORMAT_ROADMAP: ReadonlyArray<{
  format: LearningFormat;
  status: "available";
  target: "I3";
  accessibleFallbackRequired: boolean;
}> = (Object.keys(LEARNING_FORMATS) as LearningFormat[]).map((format) => ({
  format,
  status: "available",
  target: "I3",
  accessibleFallbackRequired: format === "immersiveDemo3d",
}));

export const INTEGRATED_HELP_CENTER_BOUNDARY = {
  publicRoute: "/aide/formation",
  contentAuthority: "EXPLORE",
  trainingAssetAuthority: "EXPLORE",
  financialAuthority: "GIFTSHOP",
  browserFinancialMutationAllowed: false,
  usesProductionData: false,
  containsSecrets: false,
  profiles: Object.keys(LEARNING_PROFILE_CATALOG) as LearningProfile[],
  locales: ["fr", "en"] as const,
  downloadableAssetCount: 14,
  immersiveProfileCount: 7,
} as const;
